/**
 * The live side of New webinar, as plain functions: the start times on offer,
 * the month the date picker draws, the words the
 * card shows for a chosen day, and what the host typed, kept for a reload.
 *
 * A live's day and time are the wall clock in its zone, so every date here is a
 * plain "2026-09-24" and every sum is done on the calendar, never on the
 * browser's own clock.
 */
import { LIVE_DEFAULT_LENGTH_MIN, LIVE_TIME_STEP_MIN, notesEnough } from "../../shared/live-entry";
import { patternProblem, SERIES_UNITS, type SeriesEnds, type SeriesPattern } from "../../shared/live-series";
import { wallClock, wallTimeToUtc } from "../../shared/live-time";
import { homeZone } from "../lib/zones";

type EntryMode = "rec" | "live";

/** What the host has typed on the live side. */
export interface LiveDraft {
  /** "2026-09-24", or "" before a day is picked. */
  date: string;
  /** "19:00", or "" before a time is picked. */
  time: string;
  lengthMin: number;
  /** IANA. */
  timezone: string;
  notes: string;
  /** How it repeats and when it stops; null for a one-off. */
  pattern: SeriesPattern | null;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "19:15" as the card says it: "7:15 PM". */
export function timeLabel(timeHm: string): string {
  const [h, m] = timeHm.split(":").map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(m)} ${h < 12 ? "AM" : "PM"}`;
}

/** Every quarter hour of the day. */
const START_TIMES: ReadonlyArray<{ value: string; label: string }> = Array.from(
  { length: (24 * 60) / LIVE_TIME_STEP_MIN },
  (_, i) => {
    const value = `${pad(Math.floor((i * LIVE_TIME_STEP_MIN) / 60))}:${pad((i * LIVE_TIME_STEP_MIN) % 60)}`;
    return { value, label: timeLabel(value) };
  },
);

/** The parts of a day the start times are grouped in, in the order of the day. */
const DAY_PARTS: ReadonlyArray<{ label: string; to: number }> = [
  { label: "Early morning", to: 6 * 60 },
  { label: "Morning", to: 12 * 60 },
  { label: "Afternoon", to: 17 * 60 },
  { label: "Evening", to: 24 * 60 },
];

/**
 * Every quarter hour, grouped by part of the day. On the day itself, the times
 * already gone cannot be picked.
 */
export function startTimeGroups(
  date: string,
  zone: string,
  now: number = Date.now(),
): Array<{ label: string; options: Array<{ value: string; label: string; disabled?: boolean }> }> {
  let from = 0;
  return DAY_PARTS.map((part) => {
    const lo = from;
    from = part.to;
    const options = START_TIMES.filter((t) => {
      const [h, m] = t.value.split(":").map(Number);
      const min = h * 60 + m;
      return min >= lo && min < part.to;
    }).map((t) => {
      const gone = !!date && startPassed({ date, time: t.value, timezone: zone }, now);
      return gone ? { ...t, disabled: true } : { ...t };
    });
    return { label: part.label, options };
  });
}

/** A calendar date as a UTC midnight, so sums never cross a clock change. */
function dayMs(date: string): number {
  const [y, m, d] = date.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function ymd(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** The date it is now in a zone: "today" for the live is today where the live happens. */
export function todayIn(zone: string, now: number = Date.now()): string {
  return wallClock(new Date(now).toISOString(), zone)?.date ?? ymd(now);
}

export function addDays(date: string, days: number): string {
  return ymd(dayMs(date) + days * 86_400_000);
}

/** "Thursday, September 24". */
export function dayLabel(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(dayMs(date));
}

/** "today", "tomorrow", "in 6 days". */
export function daysAway(date: string, today: string): string {
  const days = Math.round((dayMs(date) - dayMs(today)) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

/** Monday first, as the week strip and the month both read. */
function mondayIndex(date: string): number {
  return (new Date(dayMs(date)).getUTCDay() + 6) % 7;
}

/** The seven days, Monday to Sunday, of the week that holds this date. */
export function weekOf(date: string): string[] {
  const monday = addDays(date, -mondayIndex(date));
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** "Mon" for a date. */
export function weekdayShort(date: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short" }).format(dayMs(date));
}

/** "September 2026" for the month that holds this date. */
export function monthLabel(date: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "long", year: "numeric" }).format(dayMs(date));
}

/** The first of the month `months` away from the month that holds this date. */
export function shiftMonth(date: string, months: number): string {
  const [y, m] = date.split("-").map(Number);
  return ymd(Date.UTC(y, m - 1 + months, 1));
}

/** The month as the picker draws it: Monday first, with null before the 1st and after the last. */
export function monthGrid(firstOfMonth: string): Array<string | null> {
  const [y, m] = firstOfMonth.split("-").map(Number);
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = mondayIndex(`${y}-${pad(m)}-01`);
  const cells: Array<string | null> = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= days; d++) cells.push(`${y}-${pad(m)}-${pad(d)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** The day, the time and the zone: what an instant is read from. */
type WhenPicked = Pick<LiveDraft, "date" | "time" | "timezone">;

/** The start as the one instant the server stores, or null before a day and a time are picked. */
export function startAt(draft: WhenPicked): string | null {
  if (!draft.date || !draft.time) return null;
  return wallTimeToUtc(draft.date, draft.time, draft.timezone);
}

/** The start has already gone by. */
export function startPassed(draft: WhenPicked, now: number = Date.now()): boolean {
  const at = startAt(draft);
  return at !== null && Date.parse(at) <= now;
}

/** What is wrong with how a repeating draft repeats, in the host's words; null for a one-off. */
export function repeatsProblem(draft: Pick<LiveDraft, "date" | "pattern">): string | null {
  return draft.pattern && draft.date ? patternProblem(draft.pattern, draft.date) : null;
}

/** Start may be pressed: a day, a time still ahead, a way to repeat that works, and a sentence of notes. */
export function canStartLive(draft: LiveDraft, now: number = Date.now()): boolean {
  const at = startAt(draft);
  return at !== null && Date.parse(at) > now && repeatsProblem(draft) === null && notesEnough(draft.notes);
}

export function blankDraft(zone: string = homeZone()): LiveDraft {
  return { date: "", time: "", lengthMin: LIVE_DEFAULT_LENGTH_MIN, timezone: zone, notes: "", pattern: null };
}

/** A kept end, if it is one the rule can read. */
function keptEnds(raw: unknown): SeriesEnds | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as Record<string, unknown>;
  if (e.kind === "never") return { kind: "never" };
  if (e.kind === "after" && typeof e.count === "number") return { kind: "after", count: e.count };
  if (e.kind === "on" && typeof e.date === "string") return { kind: "on", date: e.date };
  return null;
}

/** A kept pattern, if it is one the rule can read. */
function keptPattern(raw: unknown): SeriesPattern | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Record<string, unknown>;
  const unit = SERIES_UNITS.find((u) => u === p.unit);
  const ends = keptEnds(p.ends);
  if (!unit || !ends || typeof p.every !== "number" || !Array.isArray(p.weekdays)) return null;
  return { unit, every: p.every, weekdays: p.weekdays.filter((d): d is number => typeof d === "number"), monthBy: p.monthBy === "day" ? "day" : "nth", ends };
}

// ── what the host typed, kept for a reload ───────────────────────────────
/** Session storage: a reload keeps it, a new tab starts clean. */
const MEMORY_KEY = "fr.newWebinar";

interface EntryMemory {
  mode: EntryMode;
  live: LiveDraft;
}

/** What this tab last had on New webinar, or the blank screen. Never throws. */
export function recallEntry(): EntryMemory {
  const blank: EntryMemory = { mode: "rec", live: blankDraft() };
  try {
    const raw = sessionStorage.getItem(MEMORY_KEY);
    if (!raw) return blank;
    const kept = JSON.parse(raw) as Partial<EntryMemory>;
    const live = { ...blank.live, ...kept.live };
    return {
      mode: kept.mode === "live" ? "live" : "rec",
      live: {
        date: typeof live.date === "string" ? live.date : "",
        time: typeof live.time === "string" ? live.time : "",
        lengthMin: typeof live.lengthMin === "number" ? live.lengthMin : LIVE_DEFAULT_LENGTH_MIN,
        timezone: typeof live.timezone === "string" && live.timezone ? live.timezone : blank.live.timezone,
        notes: typeof live.notes === "string" ? live.notes : "",
        pattern: keptPattern(live.pattern),
      },
    };
  } catch {
    return blank;
  }
}

export function keepEntry(memory: EntryMemory): void {
  try {
    sessionStorage.setItem(MEMORY_KEY, JSON.stringify(memory));
  } catch {
    // A tab that cannot store simply starts clean after a reload.
  }
}

/** Started: the next New webinar opens clean. */
export function forgetEntry(): void {
  try {
    sessionStorage.removeItem(MEMORY_KEY);
  } catch {
    // Nothing kept, nothing to forget.
  }
}

/** A file's bytes as base64, the way the start request carries it. */
export async function fileBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
